import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Button, Paper, Grid, Chip, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DiamondIcon from '@mui/icons-material/Diamond';
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Footer from './Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { pricingPlansUSD, additionalPricingUSD } from '../models/bixbitePricingUSD';
import { pricingPlansINR, additionalPricingINR } from '../models/bixbitePricingINR';
import { detectCurrency, detectCurrencySync } from '../utils/currencyDetection';

// Icon mapping
const iconMap = {
  DiamondIcon: DiamondIcon,
  StarIcon: StarIcon,
  AutoAwesomeIcon: AutoAwesomeIcon,
  WorkspacePremiumIcon: WorkspacePremiumIcon,
};

// Helper function to get icon component
const getIconComponent = (iconName) => {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent /> : <DiamondIcon />;
};

const BixbitePricing = () => {
  const theme = useTheme();
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [useINR, setUseINR] = useState(detectCurrencySync()); // Initial sync detection
  
  // Determine which pricing to use based on IP location
  useEffect(() => {
    detectCurrency().then((isINR) => {
      setUseINR(isINR);
    });
  }, []);
  
  // Get pricing data based on timezone
  const pricingPlans = useMemo(() => {
    const plans = useINR ? pricingPlansINR : pricingPlansUSD;
    // Map icon strings to actual icon components
    return plans.map(plan => ({
      ...plan,
      icon: getIconComponent(plan.icon),
    }));
  }, [useINR]);
  
  const additionalPricing = useINR ? additionalPricingINR : additionalPricingUSD;

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  const handleStartBuilding = (e) => {
    e.preventDefault();
    window.open('https://bixbite.ai/', '_blank', 'noopener,noreferrer');
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        bgcolor: theme.palette.background.default, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #e0f7f4 0%, #b2dfdb 50%, #80cbc4 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 15s ease infinite',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(0, 121, 107, 0.1)',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '5%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(0, 77, 64, 0.08)',
            filter: 'blur(80px)',
            animation: 'floatReverse 10s ease-in-out infinite',
          }}
        />

        <Box sx={{ maxWidth: 1400, mx: 'auto', position: 'relative', zIndex: 1 }}>
          {/* Main Title */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<RocketLaunchIcon sx={{ color: 'white !important' }} />}
              label="Simple & Transparent Pricing"
              sx={{
                background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                color: 'white',
                fontWeight: 600,
                px: 2,
                py: 2.5,
                borderRadius: '50px',
                mb: 3,
                boxShadow: '0 4px 20px -4px rgba(0, 121, 107, 0.4)',
                '& .MuiChip-icon': { color: 'white' },
              }}
              data-aos="fade-down"
            />
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 2, 
                color: '#004d40',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
              data-aos="fade-up"
            >
              Bixbite Pricing Overview
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
              }}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Choose the plan that best fits your needs. Scale as you grow.
            </Typography>
          </Box>

          {/* Subscription Plans */}
          <Grid container spacing={3} justifyContent="center">
            {pricingPlans.map((plan, idx) => (
              <Grid item xs={12} sm={6} lg={3} key={plan.name}>
                <Fade in timeout={300 + idx * 100}>
                  <Paper
                    elevation={0}
                    onMouseEnter={() => setHoveredPlan(idx)}
                    onMouseLeave={() => setHoveredPlan(null)}
                    sx={{
                      p: 4,
                      borderRadius: '24px',
                      bgcolor: 'white',
                      border: plan.popular 
                        ? '2px solid #00796b' 
                        : '1px solid rgba(0, 121, 107, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: hoveredPlan === idx ? 'translateY(-12px) scale(1.02)' : 'translateY(0)',
                      boxShadow: hoveredPlan === idx 
                        ? `0 30px 60px -20px ${plan.shadowColor}` 
                        : '0 4px 20px -8px rgba(0, 77, 64, 0.1)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    data-aos="fade-up"
                    data-aos-delay={idx * 100}
                  >
                    {/* Popular Badge */}
                    {plan.popular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: -32,
                          background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                          color: 'white',
                          px: 4,
                          py: 0.5,
                          transform: 'rotate(45deg)',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          boxShadow: '0 2px 10px rgba(0, 77, 64, 0.3)',
                        }}
                      >
                        POPULAR
                      </Box>
                    )}

                    {/* Plan Icon */}
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: plan.gradient,
                        color: 'white',
                        mb: 3,
                        boxShadow: `0 8px 24px -8px ${plan.shadowColor}`,
                        transition: 'all 0.3s ease',
                        transform: hoveredPlan === idx ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
                        '& svg': { fontSize: 32 },
                      }}
                    >
                      {plan.icon}
                    </Box>

                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 3,
                        color: '#1a2f2c',
                      }}
                    >
                      {plan.name}
                    </Typography>

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.palette.text.secondary,
                            mb: 0.5,
                            fontWeight: 500,
                          }}
                        >
                          Input Token Price
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 800,
                            background: plan.gradient,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                          }}
                        >
                          {plan.inputPrice}
                          <Typography 
                            component="span" 
                            sx={{ 
                              fontSize: '0.9rem', 
                              fontWeight: 500,
                              color: theme.palette.text.secondary,
                              ml: 0.5,
                            }}
                          >
                            /million
                          </Typography>
                        </Typography>
                      </Box>

                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.palette.text.secondary,
                            mb: 0.5,
                            fontWeight: 500,
                          }}
                        >
                          Output Token Price
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 800,
                            background: plan.gradient,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                          }}
                        >
                          {plan.outputPrice}
                          <Typography 
                            component="span" 
                            sx={{ 
                              fontSize: '0.9rem', 
                              fontWeight: 500,
                              color: theme.palette.text.secondary,
                              ml: 0.5,
                            }}
                          >
                            /million
                          </Typography>
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Additional Pricing Details */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 4 },
          bgcolor: 'white',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            data-aos="fade-up"
          >
            Additional Pricing Details
          </Typography>
          <Typography
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              mb: 6,
              fontSize: '1.1rem',
            }}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Applicable to all subscription plans
          </Typography>

          <Grid container spacing={4}>
            {additionalPricing.map((section, idx) => (
              <Grid item xs={12} md={6} key={section.category}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: '20px',
                    height: '100%',
                    border: '1px solid rgba(0, 121, 107, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 16px 40px -16px rgba(0, 77, 64, 0.2)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                  data-aos="fade-up"
                  data-aos-delay={idx * 100}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: '#00796b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                      }}
                    />
                    {section.category}
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {section.items.map((item, itemIdx) => (
                      <ListItem
                        key={itemIdx}
                        sx={{
                          px: 0,
                          py: 1,
                          borderBottom: itemIdx < section.items.length - 1 
                            ? '1px solid rgba(0, 121, 107, 0.05)' 
                            : 'none',
                        }}
                      >
                        <CheckCircleIcon 
                          sx={{ 
                            fontSize: 18, 
                            color: '#4db6ac', 
                            mr: 1.5,
                            flexShrink: 0,
                          }} 
                        />
                        <ListItemText 
                          primary={item}
                          primaryTypographyProps={{
                            sx: {
                              color: theme.palette.text.secondary,
                              fontSize: '0.95rem',
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          px: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #e0f7f4 0%, #b2dfdb 100%)',
          textAlign: 'center',
        }}
      >
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#004d40',
            }}
            data-aos="fade-up"
          >
            Ready to Start Building?
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              mb: 4,
              fontSize: '1.1rem',
            }}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Join thousands of developers building with Bixbite AI
          </Typography>
          <Button
            onClick={handleStartBuilding}
            variant="contained"
            size="large"
            endIcon={<RocketLaunchIcon />}
            sx={{
              px: 6,
              py: 2,
              fontWeight: 700,
              fontSize: '1.15rem',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
              boxShadow: '0 10px 35px -10px rgba(0, 121, 107, 0.5)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 16px 45px -12px rgba(0, 121, 107, 0.6)',
              },
            }}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Start Building
          </Button>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default BixbitePricing;
